const Crossmark = require('../../article-crossmark-badge/js/crossmarkBadge');
const Collateral = require('./Collateral');
const {saveAs} = require('file-saver');
const JSZip = require('jszip');
const SmartTables = require('./SmartTables');
const {BehaviorFixedHeader} = require('./helpers');
const EventManager = require('./EventManager');
const FocusTrap = require('./FocusTrap');
const ReferencesPopup = require('./ReferencesPopup');
const SectionsNav = require('./SectionsNavbar');
const StickyHeader = require('./StickyHeader');
const ToggleButton = require('./ToggleButton');
const VisualComponent = require('./VisualComponent');
const CoreViewer = require('./FigureViewer');
const MenuButtonBehavior = require('./helpers/MenuButtonBehavior');
const MathJaxLoader = require('./MathJaxLoader');
const GetFtrEntitlements = require('get-ftr/src/index').GetFtrEntitlements;
const GetFtrUpdates = require('get-ftr/src/index').GetFtrUpdates;
/*
 * Article View is managing all the view components. It passes data and decides whether to show or not to show components on basis of viewport conditions.
 * Additionally, it runs supportive scripts for the article body -> popup builders, content enhancement, etc.
 * The view component is supposed to take data from the ArticleManager but preferably should be loosely coupled.
 * We can make it listen to the article manager or make the article manager return the promise and make the view run only once all the data are retrieved.
 * That approach, however, will make us wait for all the data to be processed first, and in case we would have to make an ajax request that will be
 * very slow, so let's not do this. Instead, we are going to get single parts of content as they become available.
 *
 * */
class ArticleView extends EventManager {
    #templates = {};
    _texts = {};
    _classes = {};

    constructor(source, config, templates) {
        super();
        this.source = source;
        this.config = config;
        this.#templates = templates;

        this.s = config.classNames;
        this.topOffset = config.topOffset;
        this.escapeModals = [];
        this.focusTrap = null;

        const textsTemplate = document.getElementById('core_texts_template');
        Object.entries(textsTemplate.dataset).forEach(([key, value]) => (this._texts[key] = value));
        this._classes = config.classNames;

        this.articleFixedHeader = BehaviorFixedHeader
            ? new BehaviorFixedHeader(config.articlePageHeader, this.s)
            : null;

        // registering components
        if (!this.config.collateral.disable) {
            this.collateral = Collateral ? new Collateral(source.data, this, config, templates) : null;
        }
        this.vC = new VisualComponent(this); // adding here to use its methods as helpers. Maybe should inherit instead?

        if (this.config.isInEditor) return;

        this.sectionNavbar = SectionsNav ? new SectionsNav(source.data, this, config) : null;
        this.stHeader = StickyHeader ? new StickyHeader(this, config) : null;
        this.heroImagery = this.getArticle().querySelector('.core-hero-imagery img');
        this.crossmark = Crossmark ? new Crossmark(this, templates.crossmarkDialogTmpl) : null;

        this.goToId = this.goToId.bind(this);
    }

    initiate() {
        this.references();
        this.footnotes();
        this.collateral?.initiate();
        this.sectionNavbar?.initiate();
        this.stHeader?.initiate();
        this.initEnhancers();
        this.loadContentModifiers();
        this.addEventListeners();
        if (this.config.figureViewer.enabled && !this.config.figureViewer.useLegacyUIViewer) {
            this.viewer = CoreViewer
                ? new CoreViewer(this.source.data.figures, this, this.config.figureViewer, this.#templates)
                : null;
        }
        try {
            this.enhanceScrollableAccessibility(
                this.source.data.figures?.table?.flatMap(table => {
                    const tableWrap = table.ref.querySelector('.table-wrap');
                    const innerTable = tableWrap?.querySelector('table');
                    return [tableWrap || innerTable].filter(Boolean);
                }) ?? []
            );
            this.enhanceScrollableAccessibility(document.querySelectorAll('#article_sections_menu > ul'));
        } catch (error) {
            console.error('Error enhancing scrollable accessibility', error);
        }
        try {
            this.enhanceIFramesAccessibility();
        } catch (error) {
            console.error('Initiate iFrames accessibility enhancer', error);
        }
        this.config.getFtr.enabled && this.initializeGetFtr();

        this.afterInit();
    }

    afterInit() {
        this.collateral?.afterInit();
    }

    initEnhancers() {
        this.tabbedAbstracts();
        this.articleRelations();

        try {
            this.enhanceAuthors();
        } catch (error) {
            console.debug('Article view - add dropdown behavior to corresponding elements:', error);
        }

        try {
            this.vC.setDropdownBehavior(this.config.dropdown);
        } catch (error) {
            console.debug('Article view - set Dropdown Behavior:', error);
        }

        try {
            const config = this.config.collapsible;
            config.textKeys.forEach(key => this.vC.setCollapsibleText(config[key], key));
            config.figureKeys.forEach(key => this.vC.setCollapsibleFigures(this.source.data.figures[key], config[key]));
        } catch (error) {
            console.debug('Article view - set Collapsible elements:', error);
        }

        try {
            /*
            Any media player should be initialized after we have manipulated the DOM
            (after cloning videos to the collateral)
             */
            const host = this.source.data.figures.media[0]?.host;
            if (host === 'brightcove') {
                this.brightcoveSetup();
            } else if (host === 'kaltura') {
                this.kalturaSetup();
            }
        } catch (error) {
            console.debug('Article view - setup video player:', error);
        }

        if (this.config.isInEditor) return;
        this.focusTrap = new FocusTrap(this.config.focusTrap);
    }

    initResponsiveController() {
        const handleOrientationChange = media => {
            if (media.matches) {
                this.dispatch('mobileOn'); // Take heed. It's a local dispatcher. It does NOT dispatch DOM events.
            } else {
                this.dispatch('mobileOff');
            }
            document.body.dispatchEvent(new Event('smartResize'));
        };
        const mql = window.matchMedia('(max-width: ' + this.config.mobileBreakpoint + 'px)');
        mql.addEventListener('change', handleOrientationChange);
        handleOrientationChange(mql);
    }

    addEventListeners() {
        this.subscribeAnchorClick('a', this.goToTarget.bind(this));
        this.initResponsiveController();
        window.addEventListener('resize', this.dispatch, {passive: true});
        window.addEventListener('scroll', this.dispatch, {passive: true});
        document.body.addEventListener('keydown', this.dispatchAnchorsAction);
        document.body.addEventListener('click', e => {
            this.dispatchAnchorsAction(e);
            this.hideTooltips();
        });
        document.body.addEventListener('keydown', e => {
            if (e.keyCode === 27) {
                e.stopPropagation();
                if (this.escapeModals && this.escapeModals.length) {
                    this.escapeModals.pop()(e);
                }
            }
            this.hideTooltips();
        });

        const downloadBtn = document.querySelector('.download_pdf_supplement');

        if (downloadBtn) {
            if (!document.getElementById('supplementary-materials')) {
                downloadBtn.setAttribute('aria-disabled', 'true');
            }

            downloadBtn.addEventListener('click', async event => {
                event.preventDefault();
                this.downloadPdfandAllSupplements();
            });
        }

        if (this.config.textHighlighting.enable) {
            document.addEventListener('DOMContentLoaded', this.textHighlight());
        }
    }

    async downloadPdfandAllSupplements() {
        try {
            const supplementaryContent = document.getElementById('supplementary-materials');
            const downloadPdfUrl = document.getElementById('downloadPdfUrl');
            const validLinks = Array.from(supplementaryContent?.querySelectorAll('a[href]:not([href="#"])') || []);

            if (downloadPdfUrl) {
                validLinks.push(downloadPdfUrl);
            }

            if (validLinks.length === 0) {
                throw new Error('No valid download links found.');
            }
            const indicationMessage = document.querySelector('.downloadIndicator');
            indicationMessage?.classList.remove('hidden');

            const promises = validLinks.map(this.fetchLinkData);
            const filesData = await Promise.all(promises);

            const zip = new JSZip();
            filesData.forEach(({filename, blob}) => {
                zip.file(filename, blob);
            });

            const zipTitle = downloadPdfUrl
                ? downloadPdfUrl.getAttribute('data-doi')
                : 'pdf_and_supplementary_materials';
            const zipBlob = await zip.generateAsync({type: 'blob'});
            saveAs(zipBlob, `${zipTitle}.zip`);

            indicationMessage?.classList.add('hidden');
        } catch (error) {
            console.error('Error:', error.message);
        }
    }

    async fetchLinkData(link) {
        let filename = link.getAttribute('download');
        if (link.id === 'downloadPdfUrl') {
            let doi = link.getAttribute('data-doi');
            doi = doi.replace(/[/:]/g, '-');
            filename = `${doi}.pdf`;
        }
        const res = await fetch(link.href);
        return {filename, blob: await res.blob()};
    }

    textHighlight() {
        if (document.readyState === 'complete' || document.readyState === 'interactive') {
            const urlParams = new URLSearchParams(window.location.search);
            const startTextFragment = urlParams.get('startTxt');
            const endTextFragment = urlParams.get('endTxt');

            if (startTextFragment && endTextFragment) {
                const escapedStartTxt = startTextFragment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const startRegex = new RegExp(escapedStartTxt, 'gi');

                const escapedEndTxt = endTextFragment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const endRegex = new RegExp(escapedEndTxt + '\\b', 'gi');

                const textNodes = document.evaluate(
                    '//text()',
                    document.body,
                    null,
                    XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE,
                    null
                );

                for (let i = 0; i < textNodes.snapshotLength; i++) {
                    const node = textNodes.snapshotItem(i);
                    const text = node.nodeValue;

                    const startMatch = startRegex.exec(text);
                    const endMatch = endRegex.exec(text);

                    if (startMatch && endMatch && startMatch.index < endMatch.index) {
                        const highlightedText = text.slice(startMatch.index, endMatch.index + endMatch[0].length);

                        const span = document.createElement('span');
                        span.classList.add('searchHighlight');
                        span.textContent = highlightedText;

                        const parent = node.parentNode;
                        const beforeText = text.slice(0, startMatch.index);
                        const afterText = text.slice(endMatch.index + endMatch[0].length);

                        if (beforeText) {
                            parent.insertBefore(document.createTextNode(beforeText), node);
                        }
                        parent.insertBefore(span, node);
                        if (afterText) {
                            parent.insertBefore(document.createTextNode(afterText), node);
                        }
                        parent.removeChild(node);
                        break;
                    }
                }
            }
        }
    }

    loadContentModifiers() {
        this.loadMathJax();
        this.manageArticleVersion();
        this.loadPrismJs();

        if (this.crossmark) {
            this.crossmark.initiate();
        }

        try {
            this.smartTables = new SmartTables(this.config.smartTables, this.source.data.figures.table);
        } catch (error) {
            console.error('Initiate tables behavior enhancer', error);
        }
    }

    initializeGetFtr() {
        const {entitlements, updates} = this.config.getFtr;
        const collateralRefIdPrefix = this.config.collateral.s.prefix;

        const handleEscapeModals = (trigger, tooltip) => {
            const hideTooltip = (e) => {
                if (e.key === 'Escape') {
                    tooltip.classList.remove('tooltip-visible');
                }
            };
            trigger.addEventListener('focus', () => this.escapeModals.push(hideTooltip));
            trigger.addEventListener('blur', () => {
                if (this.escapeModals[this.escapeModals.length - 1] === hideTooltip)
                    this.escapeModals.pop();
            });
        }

        const initializeEntitlements = () => {
            const selectors = entitlements.selectors;
            new GetFtrEntitlements(selectors.body?.external, selectors.body?.internal).initialize();
            new GetFtrEntitlements(selectors.collateral?.external, selectors.collateral?.internal).initialize();
            new GetFtrEntitlements(selectors.citedBy?.external, selectors.citedBy?.internal).initialize();
            synchronizeGetFtrEntitlements();
        };
        const synchronizeGetFtrEntitlements = () => {
            const bodyButtons = document.getElementById('bibliography')
                ?.querySelectorAll('.core-xlink-crossref');
            const collateralButtons = document
                .getElementById('core-collateral-references')
                ?.querySelectorAll('.core-xlink-crossref');
            if (!bodyButtons || !collateralButtons) return;
            const mutationCallback = (mutationList) => {
                for (const mutation of mutationList) {
                    const mutationTarget = mutation.target;
                    const currentId = mutationTarget.closest('[id]').id;
                    const clonedNode = mutationTarget.cloneNode(true);
                    const isMutatedCollateral = currentId.startsWith(collateralRefIdPrefix);
                    const targetId = isMutatedCollateral? currentId.slice(collateralRefIdPrefix.length)
                        : collateralRefIdPrefix + currentId;
                    document.getElementById(targetId).querySelector('.core-xlink-crossref')
                        .replaceWith(clonedNode);
                    const clonedTrigger = clonedNode.children[0];
                    const tooltipId = clonedTrigger.getAttribute('aria-describedby');
                    if (tooltipId) {
                        const tooltip = document.getElementById(tooltipId);
                        GetFtrEntitlements.initializeTooltip(clonedTrigger, tooltip);
                        const escapeModalsTarget = isMutatedCollateral? mutationTarget : clonedNode;
                        handleEscapeModals(escapeModalsTarget.children[0], tooltip);
                    }
                }
            };
            const mutationObserver = new MutationObserver(mutationCallback);
            const observeButtons = buttons => {
                buttons.forEach(button => mutationObserver.observe(button, {attributeFilter: ['class']}));
            };
            observeButtons(bodyButtons);
            observeButtons(collateralButtons);
        };

        const initializeUpdates = () => {
            new GetFtrUpdates(updates.selectors).initialize();
            cloneUpdateButtons();
        };
        const cloneUpdateButtons = () => {
            const buttons = document.getElementById('bibliography')
                ?.querySelectorAll('.external-links');
            const mutationCallback = (mutationList) => {
                for (const mutation of mutationList) {
                    for (const node of mutation.addedNodes) {
                        if (node.classList?.contains('get-ftr-update')) {
                            const currentId = node.closest('[id]').id;
                            const clonedNode = node.cloneNode(true);
                            document.getElementById(collateralRefIdPrefix + currentId)
                                .querySelector('.core-xlink-crossref').insertAdjacentElement('afterend', clonedNode);
                            const clonedTrigger = clonedNode.children[0];
                            const tooltip = document.getElementById(clonedTrigger.getAttribute('aria-describedby'));
                            GetFtrUpdates.initializeTooltip(clonedTrigger, tooltip);
                            handleEscapeModals(clonedTrigger, tooltip);
                        }
                    }
                }
            };
            const mutationObserver = new MutationObserver(mutationCallback);
            buttons.forEach(button => mutationObserver.observe(button, {childList: true}));
        };

        entitlements.enabled && initializeEntitlements();
        updates.enabled && initializeUpdates();
    }

    getArticle() {
        return this.source.article;
    }

    loadMathJax() {
        if (window.MathJax) {
            return;
        }
        const mathjaxLoader = new MathJaxLoader(this.config.mathjax, this.getArticle());
        mathjaxLoader.loadMathJax();
    }

    addTopOverlay(val) {
        this.topOffset += val;
    }
    subtractTopOverlay(val = 0) {
        this.topOffset = Math.max(this.topOffset - val, 0);
    }

    xrefLinks(data, config, type) {
        data.forEach(link => {
            const element = link.ref;
            let slot = element.querySelector('.' + config.s.linkWrapper);
            if (!slot) {
                element.insertAdjacentHTML('beforeend', '<div class="' + config.s.linkWrapper + '"></div>');
                slot = element.querySelector('.' + config.s.linkWrapper);
            }
            const xrefLinks = this.vC.stringToHTML(
                this.#templates.xrefLinks({item: link, icons: this.config.icons, texts: this._texts, type})
            );
            slot.insertBefore(xrefLinks, slot.firstElementChild || null);
            const dropdown = slot.querySelector('.to-citation__accordion');
            !!dropdown && slot.appendChild(dropdown);
        });
    }
    references(config = this.config.citations) {
        this.xrefLinks(this.source.data.citations, config, 'citations');
        new ReferencesPopup(this, this.#templates.referencesPopup);
        // if (this.config.citations.renderAsDropBlocks) {
        //     try {
        //         const that = this;
        //         const createDropBlockMarkup = (function() {
        //             const spanWrapper = document.createElement('span');
        //             spanWrapper.classList.add('dropBlock', 'reference-citations');
        //             const amendLinkAttributes = (el, id) => {
        //                 el.classList.add('reference-citations__ctrl');
        //                 el.href = '#';
        //                 el.setAttribute('aria-expanded', false);
        //                 el.setAttribute('data-db-target-for', id);
        //                 el.setAttribute('aria-controls', id);
        //             };
        //             const addDropBlockHolder = (el, content, id) => {
        //                 return that.vC.stringToHTML(that.#templates.dropBlockTmpl({id, content}));
        //             };
        //
        //             return function(el, content) {
        //                 const wrapper = spanWrapper.cloneNode(false);
        //                 const id = el.id.replace(that.config.enhancements.bodyPrefix, '');
        //                 amendLinkAttributes(el, id);
        //                 el.parentNode.insertBefore(wrapper, el);
        //                 wrapper.appendChild(el);
        //                 wrapper.appendChild(addDropBlockHolder(el, content, id));
        //             };
        //         })();
        //
        //         this.source.data.citations.forEach(item =>
        //             item.points?.forEach(anchor => createDropBlockMarkup(anchor, item.description))
        //         );
        //         // this.UX.dropBlock.init();
        //     } catch (error) {
        //         console.error('DropBlock - citations:', error);
        //     }
        // }
    }
    footnotes(config = this.config.footnotes) {
        this.xrefLinks(this.source.data.footnotes, config, 'footnotes');
    }

    goToId(id) {
        var target = this.getArticle().querySelector(id);
        if (target) {
            const classList = target.classList;
            if (classList?.contains('citations') || classList?.contains('citation')) {
                target = target.closest(this.config.citations.s.parent);
            }

            // if the target is hidden in a collapsible parent, expand the parent and then scroll to the target
            if (this.vC.isHidden(target) && !!target.closest('[data-method]')) {
                target.closest('[data-method]').nextElementSibling?.querySelector('[data-trigger]')?.click();
            }

            if (!id.includes(this.config.figureViewer.s.prefix)) {
                // TODO is it needed?
                this.dispatch({type: 'articleInnerTargetOpen', target});
            }
            this.vC.animateTo(target);
        }
    }
    goToTarget(e) {
        if (e.currentTarget.hash && e.currentTarget.origin === document.location.origin) {
            this.goToId(e.currentTarget.hash);
            this.hideTooltips();
            if (!this.collateral?.main?.contains(this.collateral?.main.querySelector(e.currentTarget.hash))) {
                this.focusTrap?.deactivate();
            }
        }
    }

    manageArticleVersion() {
        const articleVersionLink = 'a[href="#core-collateral-versions"]';
        this.subscribeAnchorClick(articleVersionLink, this.goToTarget.bind(this));
    }

    loadPrismJs() {
        const needPrism = this.getArticle().querySelector('code[class^="lang-"], code[class^="language-"]');
        if (!this.config.prismJs.enabled || !needPrism) return;

        const loadScript = src => {
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = src;
                script.onload = resolve;
                script.onerror = reject;
                document.body.appendChild(script);
            });
        };

        const loadStylesheet = href => {
            return new Promise((resolve, reject) => {
                const link = document.createElement('link');
                link.href = href;
                link.rel = 'stylesheet';
                link.onload = resolve;
                link.onerror = reject;
                document.head.appendChild(link);
            });
        };

        const loadPrism = async () => {
            try {
                await loadStylesheet('https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism.min.css');
                await loadScript('https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js');
                await loadScript(
                    'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/autoloader/prism-autoloader.min.js'
                );
                Prism.plugins.autoloader.languages_path =
                    'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/';
                Prism.highlightAll();
            } catch (error) {
                console.error('Error loading Prism:', error);
            }
        };

        loadPrism();
    }

    hideTooltips() {
        try {
            if (jQuery && !!document.body.querySelector('[data-toggle="tooltip"]')) {
                const $tooltips = $('[data-toggle="tooltip"]');
                if ($tooltips.data('bs.tooltip')) $tooltips.tooltip('hide');
            }
        } catch (error) {
            console.error('Hide tooltips failed', error);
        }
    }

    isAccessDenial() {
        return ['no', 'limited'].includes(sessionStorage.getItem('access'));
    }

    enhanceAuthors() {
        const contributorsLines = this.getArticle().querySelectorAll(this.config.contributors.byline.CONTRIBUTORS);
        // There can be multiple contributorsLines if there are subarticles...
        if (!contributorsLines.length === 0) return;

        const hideEvent = new Event('authorsHide');
        const revealEvent = new Event('authorsReveal');

        const displayOnAttr = this.config.contributors.byline.DISPLAYED,
            onDisplayAttr = this.config.contributors.byline.ON_DISPLAY,
            hiddenOnAttr = this.config.contributors.byline.HIDDEN;

        contributorsLines.forEach(line => {
            line.querySelectorAll('[' + this.config.contributors.byline.TOGGLER + ']').forEach(toggle => {
                let event = revealEvent;
                if (toggle.dataset?.action === 'hide') {
                    event = hideEvent;
                }

                toggle.setAttribute('tabindex', '0');
                toggle.setAttribute('role', 'listitem');
                toggle.addEventListener('keypress', e => {
                    const code = e.keyCode || e.which;
                    if (code === 13 || code === 32) {
                        // enter or space
                        line.dispatchEvent(event);
                    }
                });
                toggle.addEventListener('click', () => line.dispatchEvent(event));

                const parent = toggle.closest('[' + displayOnAttr + ']');
                if (!parent) return;
                const reveal = parent.getAttribute(displayOnAttr);
                const hide = parent.getAttribute(onDisplayAttr) || 'none';
                line.addEventListener('authorsHide', () => parent.setAttribute(displayOnAttr, reveal));
                line.addEventListener('authorsReveal', () => parent.setAttribute(displayOnAttr, hide));
            });

            line.querySelectorAll('[' + displayOnAttr + ']').forEach(toggle => {
                /*
                    if it doesn't provide specific display/hide instructions,
                    and it doesn't contain an actionable item
                    manually set the hide/reveal states
                */
                if (
                    !toggle.getAttribute('data-on-display') &&
                    !toggle.querySelector('[' + this.config.contributors.byline.TOGGLER + ']')
                ) {
                    line.addEventListener('authorsHide', () => toggle.setAttribute(displayOnAttr, 'all'));
                    line.addEventListener('authorsReveal', () => toggle.setAttribute(displayOnAttr, 'none'));
                }
            });

            line.querySelectorAll('[' + hiddenOnAttr + ']').forEach(author => {
                let reveal = 'none';
                let hide = author.getAttribute(hiddenOnAttr);
                line.addEventListener('authorsHide', () => author.setAttribute(hiddenOnAttr, hide));
                line.addEventListener('authorsReveal', () => author.setAttribute(hiddenOnAttr, reveal));
            });
        });
    }

    enhanceScrollableAccessibility(elements) {
        elements?.forEach(element => {
            if (element) {
                const hasHorizontalScrollbar = element.scrollWidth > element.clientWidth;
                const hasVerticalScrollbar = element.scrollHeight > element.clientHeight;

                if (hasHorizontalScrollbar || hasVerticalScrollbar) {
                    element.setAttribute('tabindex', '0');
                }
            }
        });
    }

    enhanceIFramesAccessibility() {
        if (!this.config.enhancements.enhanceIframeAccessibility) return;

        const iframes = document.querySelectorAll('div.figure-wrap iframe');
        if (!iframes.length) return;
        iframes.forEach(iframe => {
            let parent = iframe.parentElement;
            let counter = 0;
            const maxTraversal = 7;

            while (parent && counter < maxTraversal) {
                if (parent.tagName.toLowerCase() === 'figure' && parent.id) {
                    iframe.setAttribute('aria-labelledby', parent.id);
                    break;
                }

                if (parent.tagName.toLowerCase() === 'section') {
                    break;
                }

                parent = parent.parentElement;
                counter++;
            }
        });
    }

    brightcoveSetup() {
        const brightcoveTmpl = document.getElementById('brightcove-template');
        if (brightcoveTmpl) {
            const scriptTmpl = document.importNode(brightcoveTmpl.content, true);
            this.brightcoveScript = document.createElement('script');
            this.brightcoveScript.src = scriptTmpl.querySelector('script').src;
            this.brightcoveScript.id = 'core-brightcove-script';
            document.head.appendChild(this.brightcoveScript);
            this.brightcoveScript.onload = function () {
                console.debug('Brightcove script loaded');
            };
        }
    }

    kalturaSetup() {
        this.kalturaScript = document.createElement('script');
        const partnerId = this.config.videoPlayer.kaltura.partnerId;
        const uiConfId = this.config.videoPlayer.kaltura.uiConfId;
        const src = this.config.videoPlayer.kaltura.src;
        if (!src || !partnerId || !uiConfId) return;
        this.kalturaScript.src = `${src}/p/${partnerId}/embedPlaykitJs/uiconf_id/${uiConfId}`;
        this.kalturaScript.id = 'core-kaltura-script';

        this.kalturaScript.onload = () => {
            const observer = this.kalturaObserver();
            const divs = document.querySelectorAll(this.config.videoPlayer.kaltura.selector);
            divs.forEach(div => {
                observer.observe(div);
            });
        };
        document.head.appendChild(this.kalturaScript);
    }
    kalturaObserver() {
        return new IntersectionObserver((entries, observerInstance) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('activated')) {
                    this.kalturaSecondScript(entry.target);
                    entry.target.classList.add('activated');
                    observerInstance.unobserve(entry.target);
                }
            });
        });
    }
    kalturaSecondScript(div) {
        const partnerId = this.config.videoPlayer.kaltura.partnerId;
        const uiConfId = this.config.videoPlayer.kaltura.uiConfId;
        const targetId = div.id;
        const entryId = div.getAttribute('data-video-id');
        const secondScript = document.createElement('script');

        if (!targetId || !partnerId || !uiConfId || !entryId) return;

        secondScript.textContent = `
                            try {
                                var kalturaPlayer = KalturaPlayer.setup({
                                    targetId: "${targetId}",
                                    provider: {
                                        partnerId: "${partnerId}",
                                        uiConfId: "${uiConfId}"
                                    }
                                });
                                kalturaPlayer.loadMedia({entryId: "${entryId}"});
                            } catch (e) {
                                console.error(e.message);
                            }
                        `;
        document.head.appendChild(secondScript);
    }

    tabbedAbstracts() {
        // initiate tabbed multi-language abstracts in article
        const wrapper = this.getArticle().querySelector('[data-core-tabs="abstracts"]');
        this.vC.setTabBehavior(wrapper, this.config.tabs.abstracts);
    }

    articleRelations() {
        const hasTabbedArticleRelations = document.body.querySelector('[data-core-tabs="relations"]');
        hasTabbedArticleRelations && this.tabbedArticleRelations();
        const hasGroupedArticleRelations = document.body.querySelector('.relation [data-toggle-btn]');
        hasGroupedArticleRelations && this.compoundArticleRelations();
        this.inlineArticleRelations(document.body.querySelector('.inline-core-relations'));
    }
    tabbedArticleRelations() {
        const wrapper = this.getArticle().querySelector('.core-relations');
        this.vC.setTabBehavior(wrapper, this.config.tabs.articleRelations);
    }
    compoundArticleRelations() {
        // TODO check if it can be replaced with MenuButtonBehavior - pnas
        const wrapperSelector = '.core-relations .relation';

        try {
            new ToggleButton(wrapperSelector);
        } catch (error) {
            console.error('Compound article relations: ', error);
        }
    }
    inlineArticleRelations(wrapper) {
        try {
            wrapper && new MenuButtonBehavior(wrapper);
        } catch (error) {
            console.error('Inline article relations: ', error);
        }
    }
}

module.exports = ArticleView;
